import * as React from 'react';

import { type NonEmptyArray } from '@/packages/JsonQuery';

import { type TModifier } from '../../types/modifier';
import { FiltersContextProvider, type IContext } from '../../utils/context';
import { modifyJsonQuery } from '../../utils/modifyJsonQuery';
import { useFiltersOpenedState } from '../../utils/useFiltersOpenedState';

import { type IProviderProps } from './types';

export const FiltersContext: React.FC<React.PropsWithChildren<IProviderProps>> = ({
  children,
  config,
  currentLocation,
  highways,
  isTooltipAvailable,
  jsonQuery,
  jsonQueryCount,
  jsonQueryCountRefreshing,
  queryString,
  specialPromo,
  geoTags,
  isProfessionalSearchButtonVisible,
  villageName,
  onApply,
  onApplyClick,
  onChange,
  onClear,
  onDistrictPopupOpen,
  onGeoPopupOpen,
  onHighwaysPopupOpen,
  onOpenFilter,
  onUndergroundPopupOpen,
  onRegionalDistrictsOpen,
}) => {
  const { openedFilters, openFilter, closeFilter } = useFiltersOpenedState({ onOpenFilter });
  const [lastAppliedModifiers, setLastAppliedModifiers] = React.useState<NonEmptyArray<TModifier> | null>(null);

  const handleChange = React.useCallback(
    (...modifiers: NonEmptyArray<TModifier>) => {
      const nextJsonQuery = modifyJsonQuery(jsonQuery, modifiers);

      setLastAppliedModifiers(
        prevModifiers => (prevModifiers?.concat(modifiers) ?? modifiers) as NonEmptyArray<TModifier>,
      );

      onChange(nextJsonQuery);
    },
    [jsonQuery, onChange],
  );

  const handleApply = React.useCallback(() => {
    onApply();

    setLastAppliedModifiers(null);
  }, [onApply]);

  const handleApplyOpenableFilter = React.useCallback(
    (...modifiers: NonEmptyArray<TModifier>) => {
      setLastAppliedModifiers(prev => (prev?.concat(modifiers) ?? modifiers) as NonEmptyArray<TModifier>);

      handleApply();
    },
    [handleApply],
  );

  const context = React.useMemo<IContext>(
    () => ({
      config,
      currentLocation,
      highways,
      isTooltipAvailable,
      jsonQuery,
      jsonQueryCount,
      jsonQueryCountRefreshing,
      queryString,
      lastAppliedModifiers,
      openedFilters,
      specialPromo: specialPromo || null,
      geoTags,
      isProfessionalSearchButtonVisible,
      villageName,
      onApply: handleApply,
      onApplyOpenableFilter: handleApplyOpenableFilter,
      onApplyClick,
      onChange: handleChange,
      onClear,
      onDistrictPopupOpen,
      onGeoPopupOpen,
      onHighwaysPopupOpen,
      openFilter,
      closeFilter,
      onUndergroundPopupOpen,
      onRegionalDistrictsOpen,
    }),
    [
      config,
      currentLocation,
      highways,
      isTooltipAvailable,
      jsonQuery,
      jsonQueryCount,
      jsonQueryCountRefreshing,
      queryString,
      lastAppliedModifiers,
      openedFilters,
      specialPromo,
      geoTags,
      isProfessionalSearchButtonVisible,
      villageName,
      handleApply,
      handleApplyOpenableFilter,
      onApplyClick,
      handleChange,
      onClear,
      onDistrictPopupOpen,
      onGeoPopupOpen,
      onHighwaysPopupOpen,
      openFilter,
      closeFilter,
      onUndergroundPopupOpen,
      onRegionalDistrictsOpen,
    ],
  );

  return <FiltersContextProvider value={context}>{children}</FiltersContextProvider>;
};
